import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mx from "@/locales/mx.json";
import en from "@/locales/en.json";

const DICTIONARIES = { mx, en };

const I18nContext = createContext({
  lang: "mx",
  t: (key) => key,
  switchLang: () => {},
});

function getFromDict(dict, key) {
  const parts = key.split(".");
  let value = dict;
  for (const part of parts) {
    if (value && typeof value === "object" && part in value) {
      value = value[part];
    } else {
      return undefined;
    }
  }
  return value;
}

export const I18nProvider = ({ lang, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const value = useMemo(() => {
    const dict = DICTIONARIES[lang] || DICTIONARIES.mx;

    const t = (key) => {
      const fromLang = getFromDict(dict, key);
      if (fromLang !== undefined) return fromLang;
      const fallback = getFromDict(DICTIONARIES.mx, key);
      return fallback !== undefined ? fallback : key;
    };

    const switchLang = (nextLang) => {
      if (nextLang === lang) return;
      const path = location.pathname;
      let nextPath = path;
      if (path.startsWith("/mx")) {
        nextPath = path.replace(/^\/mx/, `/${nextLang}`);
      } else if (path.startsWith("/en")) {
        nextPath = path.replace(/^\/en/, `/${nextLang}`);
      } else {
        nextPath = `/${nextLang}`;
      }
      if (!nextPath || nextPath === "") nextPath = `/${nextLang}`;
      // Preserve scroll on language switch
      navigate(nextPath + location.search + location.hash, {
        state: { keepScroll: true },
      });
    };

    return { lang, t, switchLang };
  }, [lang, location, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
